import React from 'react';
import { CalculatorIcon, TrendingUpIcon, AlertCircleIcon, CheckCircle2Icon, Building2Icon, ArrowRightIcon, DollarSignIcon, CheckIcon, ChevronDownIcon, ShieldCheckIcon } from 'lucide-react';
import { RoofType, RoofTypeConfig, ROOF_TYPES } from '../App';
interface RoofingCalculatorProps {
  squares: number;
  setSquares: (value: number) => void;
  sellingPrice: number;
  setSellingPrice: (value: number) => void;
  roofType: RoofType;
  setRoofType: (value: RoofType) => void;
  roofConfig: RoofTypeConfig;
  onViewComparison: () => void;
}
export function RoofingCalculator({
  squares,
  setSquares,
  sellingPrice,
  setSellingPrice,
  roofType,
  setRoofType,
  roofConfig,
  onViewComparison
}: RoofingCalculatorProps) {
  const totalCost = squares * roofConfig.costPerSq;
  const minPricePerSq = roofConfig.costPerSq + roofConfig.targetMargin / squares;
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };
  const allRoofTypes = Object.values(ROOF_TYPES);
  return <div className="min-h-screen bg-slate-50 text-slate-900 font-sans pb-20" data-id="element-2">
      {/* Header */}
      <header className="bg-[#1B5E3B] text-white py-6 shadow-md" data-id="element-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between" data-id="element-4">
          <div className="flex items-center space-x-3" data-id="element-5">
            <img src="https://cdn.magicpatterns.com/uploads/pKiHu7huFTB332wjVWDXnG/rl1.png" alt="Relentless Construction Logo" className="h-10 w-10 rounded-md object-cover" data-id="element-6" />
            <h1 className="text-2xl font-bold tracking-tight" data-id="element-7">
              Relentless Construction
            </h1>
          </div>
          <div className="hidden sm:flex items-center space-x-2 text-green-100 text-sm font-medium" data-id="element-8">
            <CalculatorIcon className="h-4 w-4" data-id="element-9" />
            <span data-id="element-10">Pricing Dashboard</span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8" data-id="element-11">
        {/* Top Grid: Input & Margin Guidance */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-12">
          {/* Input Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8" data-id="element-13">
            <div className="flex items-center space-x-3 mb-6" data-id="element-14">
              <div className="bg-green-100 p-2 rounded-lg" data-id="element-15">
                <Building2Icon className="h-6 w-6 text-[#1B5E3B]" data-id="element-16" />
              </div>
              <h2 className="text-xl font-semibold text-slate-800" data-id="element-17">
                Project Details
              </h2>
            </div>

            <div className="space-y-6" data-id="element-18">
              {/* Roof Type Selector */}
              <div data-id="element-19">
                <label htmlFor="roof-type" className="block text-sm font-medium text-slate-600 mb-2" data-id="element-20">
                  Roof Type
                </label>
                <div className="relative" data-id="element-21">
                  <select id="roof-type" value={roofType} onChange={e => setRoofType(e.target.value as RoofType)} className="w-full appearance-none bg-white border-2 border-slate-200 rounded-xl px-4 py-3 pr-10 text-slate-800 font-semibold text-lg focus:border-[#1B5E3B] focus:ring-0 transition-colors cursor-pointer" data-id="element-22">
                    {allRoofTypes.map(rt => <option key={rt.id} value={rt.id} disabled={!rt.available} data-id="element-23">
                        {rt.label}
                        {!rt.available ? ' (Coming Soon)' : ''}
                        {rt.warranty ? ` — ${rt.warranty}` : ''}
                      </option>)}
                  </select>
                  <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" data-id="element-24" />
                </div>
                {roofConfig.warranty && <div className="mt-2 inline-flex items-center bg-amber-50 text-amber-800 text-xs font-semibold px-3 py-1.5 rounded-full border border-amber-200" data-id="element-25">
                    <ShieldCheckIcon className="h-3.5 w-3.5 mr-1.5" data-id="element-26" />
                    {roofConfig.warranty}
                  </div>}
              </div>

              {/* Number of Squares */}
              <div data-id="element-27">
                <div className="flex justify-between items-end mb-4" data-id="element-28">
                  <label htmlFor="squares-input" className="block text-sm font-medium text-slate-600" data-id="element-29">
                    Number of Squares
                  </label>
                  <div className="flex items-center space-x-2" data-id="element-30">
                    <input id="squares-input" type="number" min="1" max="100" value={squares} onChange={e => setSquares(Math.max(1, Number(e.target.value)))} className="w-24 text-right text-2xl font-bold text-[#1B5E3B] border-b-2 border-green-200 focus:border-[#1B5E3B] focus:ring-0 p-1 bg-transparent transition-colors" data-id="element-31" />
                    <span className="text-slate-500 font-medium" data-id="element-32">sq</span>
                  </div>
                </div>

                <input type="range" min="15" max="50" step="1" value={squares} onChange={e => setSquares(Number(e.target.value))} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-[#1B5E3B]" data-id="element-33" />
                <div className="flex justify-between text-xs text-slate-400 mt-2 font-medium" data-id="element-34">
                  <span data-id="element-35">15 sq</span>
                  <span data-id="element-36">50 sq</span>
                </div>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 flex justify-between items-center" data-id="element-37">
                <span className="text-slate-600 font-medium" data-id="element-38">
                  Base Cost (@ {formatCurrency(roofConfig.costPerSq)}/sq)
                </span>
                <span className="text-xl font-bold text-slate-800" data-id="element-39">
                  {formatCurrency(totalCost)}
                </span>
              </div>
            </div>
          </div>

          {/* Margin Guidance Section */}
          <div className="bg-gradient-to-br from-[#1B5E3B] to-[#113f27] rounded-2xl shadow-lg p-6 sm:p-8 text-white relative overflow-hidden" data-id="element-40">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 opacity-10" data-id="element-41">
              <TrendingUpIcon className="h-48 w-48" data-id="element-42" />
            </div>

            <div className="relative z-10 h-full flex flex-col justify-between" data-id="element-43">
              <div data-id="element-44">
                <div className="flex items-center space-x-2 mb-2" data-id="element-45">
                  <AlertCircleIcon className="h-5 w-5 text-green-300" data-id="element-46" />
                  <h2 className="text-green-200 font-medium uppercase tracking-wider text-sm" data-id="element-47">
                    Margin Target — {roofConfig.label}
                  </h2>
                </div>
                <p className="text-green-100 text-sm mb-6" data-id="element-48">
                  Minimum price per square required to maintain a{' '}
                  <strong className="text-white" data-id="element-49">
                    ${roofConfig.targetMargin.toLocaleString()}
                  </strong>{' '}
                  profit margin for this project size.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20" data-id="element-50">
                <div className="flex items-baseline space-x-2" data-id="element-51">
                  <span className="text-5xl font-extrabold tracking-tight" data-id="element-52">
                    {formatCurrency(minPricePerSq)}
                  </span>
                  <span className="text-green-200 font-medium" data-id="element-53">/ sq</span>
                </div>
                <div className="mt-4 flex items-center text-sm text-green-200" data-id="element-54">
                  <CheckCircle2Icon className="h-4 w-4 mr-1.5 text-green-400" data-id="element-55" />
                  <span data-id="element-56">Target margin achievable at or above this price</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Selling Price Selector */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8" data-id="element-57">
          <div className="flex items-center space-x-3 mb-6" data-id="element-58">
            <div className="bg-emerald-100 p-2 rounded-lg" data-id="element-59">
              <DollarSignIcon className="h-6 w-6 text-[#1B5E3B]" data-id="element-60" />
            </div>
            <div data-id="element-61">
              <h2 className="text-xl font-semibold text-slate-800" data-id="element-62">
                Select Selling Price
              </h2>
              <p className="text-sm text-slate-500 mt-0.5" data-id="element-63">
                Choose the price per square for this {roofConfig.label} project.
                Only prices meeting the $6k margin are available.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3" data-id="element-64">
            {roofConfig.sellingPrices.map(price => {
            const profit = (price - roofConfig.costPerSq) * squares;
            const meetsMargin = profit >= roofConfig.targetMargin;
            const isSelected = sellingPrice === price;
            return <button key={price} onClick={() => meetsMargin && setSellingPrice(price)} disabled={!meetsMargin} className={`relative rounded-xl p-4 text-center transition-all duration-150 border-2 ${isSelected ? 'border-[#1B5E3B] bg-[#1B5E3B] text-white shadow-lg scale-[1.02]' : meetsMargin ? 'border-slate-200 bg-white text-slate-800 hover:border-[#1B5E3B]/40 hover:shadow-sm cursor-pointer' : 'border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed'}`} data-id="element-65">
                  {isSelected && <div className="absolute -top-2 -right-2 bg-white rounded-full p-0.5 shadow-md" data-id="element-66">
                      <CheckIcon className="h-4 w-4 text-[#1B5E3B]" data-id="element-67" />
                    </div>}
                  <div className={`text-2xl font-bold ${isSelected ? 'text-white' : meetsMargin ? 'text-slate-800' : 'text-slate-300'}`} data-id="element-68">
                    {formatCurrency(price)}
                  </div>
                  <div className={`text-xs font-medium mt-1 ${isSelected ? 'text-green-100' : meetsMargin ? 'text-slate-500' : 'text-slate-300'}`} data-id="element-69">
                    per square
                  </div>
                  <div className={`text-xs font-semibold mt-2 ${isSelected ? 'text-green-200' : meetsMargin ? 'text-green-600' : 'text-red-300'}`} data-id="element-70">
                    {meetsMargin ? `${formatCurrency(profit)} profit` : 'Below margin'}
                  </div>
                </button>;
          })}
          </div>

          {/* Selected price summary */}
          <div className="mt-6 bg-slate-50 rounded-xl p-4 border border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-71">
            <div className="text-sm text-slate-600" data-id="element-72">
              <span className="font-medium" data-id="element-73">Selected:</span>{' '}
              {formatCurrency(sellingPrice)}/sq × {squares} squares
            </div>
            <div className="flex items-center space-x-4" data-id="element-74">
              <div className="text-sm" data-id="element-75">
                <span className="text-slate-500" data-id="element-76">Total: </span>
                <span className="font-bold text-slate-800" data-id="element-77">
                  {formatCurrency(sellingPrice * squares)}
                </span>
              </div>
              <div className="text-sm" data-id="element-78">
                <span className="text-slate-500" data-id="element-79">Profit: </span>
                <span className="font-bold text-green-700" data-id="element-80">
                  {formatCurrency(sellingPrice * squares - totalCost)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Table Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" data-id="element-81">
          <div className="p-6 border-b border-slate-200 bg-slate-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-82">
            <div className="flex items-center space-x-3" data-id="element-83">
              <div className="bg-green-100 p-2 rounded-lg" data-id="element-84">
                <CalculatorIcon className="h-6 w-6 text-green-700" data-id="element-85" />
              </div>
              <h2 className="text-xl font-semibold text-slate-800" data-id="element-86">
                Profitability Matrix — {roofConfig.label}
              </h2>
            </div>
            <div className="flex items-center text-sm text-slate-600 bg-white px-3 py-1.5 rounded-full border border-slate-200 shadow-sm" data-id="element-87">
              <span className="w-3 h-3 rounded-full bg-green-100 border border-green-300 mr-2" data-id="element-88"></span>
              Meets $6k Margin
            </div>
          </div>

          <div className="overflow-x-auto" data-id="element-89">
            <table className="w-full text-left border-collapse" data-id="element-90">
              <thead data-id="element-91">
                <tr data-id="element-92">
                  <th className="p-4 border-b border-slate-200 bg-white font-semibold text-slate-500 w-48" data-id="element-93">
                    Selling Price / sq
                  </th>
                  {roofConfig.sellingPrices.map(price => <th key={price} className={`p-4 border-b border-slate-200 font-bold text-right min-w-[120px] ${price === sellingPrice ? 'bg-[#1B5E3B]/5 text-[#1B5E3B] border-b-2 border-b-[#1B5E3B]' : 'bg-white text-slate-800'}`} data-id="element-94">
                      {formatCurrency(price)}
                      {price === sellingPrice && <span className="block text-xs font-medium text-[#1B5E3B] mt-0.5" data-id="element-95">
                          Selected
                        </span>}
                    </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-96">
                <tr data-id="element-97">
                  <td className="p-4 font-medium text-slate-600" data-id="element-98">
                    Total Price
                  </td>
                  {roofConfig.sellingPrices.map(price => <td key={price} className={`p-4 text-right font-medium ${price === sellingPrice ? 'bg-[#1B5E3B]/5 text-[#1B5E3B] font-bold' : 'text-slate-800'}`} data-id="element-99">
                      {formatCurrency(price * squares)}
                    </td>)}
                </tr>

                <tr data-id="element-100">
                  <td className="p-4 font-medium text-slate-600" data-id="element-101">Total Cost</td>
                  {roofConfig.sellingPrices.map(price => <td key={price} className={`p-4 text-right ${price === sellingPrice ? 'bg-[#1B5E3B]/5 text-slate-600' : 'text-slate-500'}`} data-id="element-102">
                      {formatCurrency(totalCost)}
                    </td>)}
                </tr>

                <tr data-id="element-103">
                  <td className="p-4 font-bold text-slate-800 bg-slate-50" data-id="element-104">
                    Profit
                  </td>
                  {roofConfig.sellingPrices.map(price => {
                  const profit = price * squares - totalCost;
                  const meetsMargin = profit >= roofConfig.targetMargin;
                  const isSelected = price === sellingPrice;
                  return <td key={price} className={`p-4 text-right font-bold transition-colors ${isSelected ? 'bg-[#1B5E3B] text-white' : meetsMargin ? 'bg-green-50 text-green-700 border-l border-r border-green-100' : 'text-slate-800 bg-slate-50'}`} data-id="element-105">
                        {formatCurrency(profit)}
                      </td>;
                })}
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Competitor Comparison CTA */}
        <button onClick={onViewComparison} className="w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8 text-left group cursor-pointer hover:border-[#1B5E3B]/30 hover:shadow-md transition-all duration-200" data-id="element-106">
          <div className="flex items-center justify-between" data-id="element-107">
            <div className="flex items-center space-x-4" data-id="element-108">
              <div className="bg-orange-100 p-3 rounded-xl" data-id="element-109">
                <TrendingUpIcon className="h-7 w-7 text-orange-700" data-id="element-110" />
              </div>
              <div data-id="element-111">
                <h2 className="text-xl font-semibold text-slate-800 mb-1" data-id="element-112">
                  Compare Our Pricing
                </h2>
                <p className="text-sm text-slate-500" data-id="element-113">
                  See how Relentless Construction stacks up against Master
                  Roofing and Dabella
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3" data-id="element-114">
              <div className="hidden sm:flex items-center space-x-3 mr-4" data-id="element-115">
                <span className="flex items-center text-xs font-medium text-slate-500" data-id="element-116">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#1B5E3B] mr-1.5" data-id="element-117"></span>
                  Relentless
                </span>
                <span className="flex items-center text-xs font-medium text-slate-500" data-id="element-118">
                  <span className="w-2.5 h-2.5 rounded-full bg-slate-700 mr-1.5" data-id="element-119"></span>
                  Master Roofing
                </span>
                <span className="flex items-center text-xs font-medium text-slate-500" data-id="element-120">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-800 mr-1.5" data-id="element-121"></span>
                  Dabella
                </span>
              </div>
              <div className="bg-[#1B5E3B] text-white p-2 rounded-lg group-hover:bg-[#113f27] transition-colors" data-id="element-122">
                <ArrowRightIcon className="h-5 w-5" data-id="element-123" />
              </div>
            </div>
          </div>
        </button>
      </main>
    </div>;
}